import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { HiExclamationCircle, HiOutlineCheck, HiPlus } from "react-icons/hi";
import { LuChevronDown, LuTriangleAlert } from "react-icons/lu";
import { LinearLoadingOverlay } from "../Loading";
import PatientSection from "./PatientSection";
import PrescriptionCard from "./PrescriptionCard";
import CommentsSection from "./CommentsSection";
import QuestionsSection from "./QuestionsSection";
import SamplingSection from "./SamplingSection";
import StructureSection from "./StructureSection";
import MutuelleSection from "./MutuelleSection";
import CorrespondantsSection from "./CorrespondantsSection";
import AdditionalDoctorsSection from "./AdditionalDoctorsSection";
import RecommendationsSection from "./RecommendationsSection";
import AddPrescriptionModal from "./AddPrescriptionModal";
import BatchDeletePrescriptionsModal from "./BatchDeletePrescriptionsModal";
import MergePrescriptionsModal from "../MergePrescriptionsModal";
import SelectedPrescriptionsBar from "./SelectedPrescriptionsBar";
import AdmissionCodeContextMenu from "./AdmissionCodeContextMenu";
import { TERMINAL_ADMISSION_STATUSES, conflictsWith, isRequiredFieldState, sameFieldValue } from "../../utils";

const DEFAULT_CONTEXT_MENU = { visible: false, x: 0, y: 0, allSelectedAreALD: false, allSelectedAreRenewal: false };
const IDENTITY_LABELS = { firstName: "Prénom", birthLastName: "Nom de naissance", sexe: "Sexe", birthdate: "Date de naissance", birthplace: "Lieu de naissance" };

const AdmissionOverview = forwardRef(
  (
    {
      admission,
      isActive,
      activeFields,
      validationErrors,
      showProductionRequired,
      laboratories,
      autocompleteSettings,
      actionsAppliedToPrescriptions,
      incompatibleCodesRefused,
      isBackgroundLoading,
      onAdmissionChange,
      onScrollToImage,
      ProcessingStatusIcon,
      onAdmissionUpdated,
      onGetAdmissionDetails,
      onSetBackgroundLoading,
      onGuidelineValidatedChange,
    },
    ref,
  ) => {
    const [selectedPrescriptions, setSelectedPrescriptions] = useState(new Set());
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [contextMenu, setContextMenu] = useState(DEFAULT_CONTEXT_MENU);
    const [renewalPopover, setRenewalPopover] = useState(null);
    const [showAddPrescriptionModal, setShowAddPrescriptionModal] = useState(false);
    const [showPrescriptionMergeModal, setShowPrescriptionMergeModal] = useState(false);
    const [prescriptionBatchLoading, setPrescriptionBatchLoading] = useState(false);
    const [showBatchDeletePrescriptionsModal, setShowBatchDeletePrescriptionsModal] = useState(false);
    const [dedupMatches, setDedupMatches] = useState([]);
    const [selectedMatchId, setSelectedMatchId] = useState(null);
    const [conflictChoices, setConflictChoices] = useState({});
    const [isPrescriptionLoading, setIsPrescriptionLoading] = useState(false);
    const patientComments = admission?.patient?.comments?.filter((comment) => comment?.trim()) || [];
    const isLocked = TERMINAL_ADMISSION_STATUSES.includes(admission?.status);

    useEffect(() => {
      setSelectedPrescriptions((prev) => (prev.size === 0 ? prev : new Set()));
      setSelectedCodes([]);
      setContextMenu(DEFAULT_CONTEXT_MENU);
      setRenewalPopover(null);
      setShowAddPrescriptionModal(false);
      setShowPrescriptionMergeModal(false);
      setPrescriptionBatchLoading(false);
      setShowBatchDeletePrescriptionsModal(false);
      setDedupMatches([]);
      setSelectedMatchId(null);
      setIsPrescriptionLoading(false);
    }, [admission?._id]);

    useEffect(() => {
      if (selectedMatchId !== null && !dedupMatches.some((match) => String(match._id) === selectedMatchId)) setSelectedMatchId(null);
    }, [dedupMatches, selectedMatchId]);

    useEffect(() => setConflictChoices({}), [selectedMatchId]);

    const selectedMatch = dedupMatches.find((match) => String(match._id) === selectedMatchId) || null;
    const isMerging = selectedMatch !== null;
    const conflicts = isMerging
      ? conflictsWith(admission?.patient, selectedMatch, activeFields).map((conflict) => {
          if (conflict.isLocked) return { ...conflict, choice: "fiche" };
          if (conflictChoices[conflict.key]) return { ...conflict, choice: conflictChoices[conflict.key] };
          if (conflict.current === "") return { ...conflict, choice: "fiche" };
          return conflict;
        })
      : [];
    const choosableConflicts = conflicts.filter((conflict) => !conflict.isLocked);
    const overriddenIdentityLabels = conflicts.filter((conflict) => conflict.isLocked).map((conflict) => IDENTITY_LABELS[conflict.key]);
    const pendingChoiceCount = conflicts.filter((conflict) => conflict.choice === undefined).length;

    const onLinkPatient = () => {
      const applied = conflicts.filter((conflict) => conflict.choice === "fiche");
      const insFollowsDb = sameFieldValue(admission?.patient?.INS?.value, selectedMatch.INS?.value) || applied.some((conflict) => conflict.key === "INS");

      setSelectedMatchId(null);
      setDedupMatches([]);
      onAdmissionChange([
        { field: "flags", value: (admission?.flags || []).filter((flag) => flag !== "PATIENT_INFO_ALREADY_EXISTS") },
        { field: "patient.patientId", value: selectedMatch.patientId || "" },
        { field: "patient.silPatientNumber", value: selectedMatch.silPatientNumber || "" },
        { field: "patient.INS.isQualified", value: insFollowsDb ? selectedMatch.INS?.isQualified === true : false },
        ...applied.flatMap((conflict) => conflict.changes(selectedMatch)),
      ]);
    };

    useEffect(() => {
      if (contextMenu.visible !== true) return;

      const onWindowClick = () => setContextMenu((prev) => ({ ...prev, visible: false, x: 0, y: 0 }));

      window.addEventListener("click", onWindowClick);
      return () => window.removeEventListener("click", onWindowClick);
    }, [contextMenu.visible]);

    useImperativeHandle(
      ref,
      () => ({
        closeUi: () => {
          let hasClosedUi = false;

          if (selectedMatchId !== null) {
            setSelectedMatchId(null);
            hasClosedUi = true;
          }

          if (contextMenu.visible === true) {
            setContextMenu(DEFAULT_CONTEXT_MENU);
            hasClosedUi = true;
          }

          if (showAddPrescriptionModal === true) {
            setShowAddPrescriptionModal(false);
            hasClosedUi = true;
          }

          if (showPrescriptionMergeModal === true) {
            setShowPrescriptionMergeModal(false);
            hasClosedUi = true;
          }

          if (showBatchDeletePrescriptionsModal === true) {
            setShowBatchDeletePrescriptionsModal(false);
            hasClosedUi = true;
          }

          return hasClosedUi;
        },
      }),
      [selectedMatchId, contextMenu.visible, showAddPrescriptionModal, showPrescriptionMergeModal, showBatchDeletePrescriptionsModal],
    );

    const onTogglePrescriptionSelection = (prescriptionId) => {
      const nextSelection = new Set(selectedPrescriptions);
      if (nextSelection.has(prescriptionId)) nextSelection.delete(prescriptionId);
      else nextSelection.add(prescriptionId);
      setSelectedPrescriptions(nextSelection);
    };

    const isPrescriptionMissing =
      (admission?.prescriptions || []).length === 0 && ["silPrescriptionNumber", "date", "doctor"].some((key) => isRequiredFieldState(activeFields?.prescription?.[key]));

    return (
      <>
        {isActive === true && (
          <div className="relative px-8 py-6">
            <LinearLoadingOverlay isVisible={isPrescriptionLoading} />

            <div inert={isMerging ? "" : undefined} className={`transition-opacity ${isMerging ? "opacity-40" : ""}`}>
              {patientComments.length > 0 && (
                <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-2 text-[12px]">
                  <HiExclamationCircle className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                  <span className="font-medium uppercase tracking-wider text-red-600">Attention</span>
                  <span className="text-gray-300">—</span>
                  <span className="truncate text-gray-700">{patientComments.join(" · ")}</span>
                </div>
              )}

              <StructureSection
                admission={admission}
                activeFields={activeFields}
                laboratories={laboratories}
                onAdmissionChange={onAdmissionChange}
                ProcessingStatusIcon={ProcessingStatusIcon}
              />
            </div>

            <div className="mb-4">
              <PatientSection
                admission={admission}
                activeFields={activeFields}
                validationErrors={validationErrors}
                onAdmissionChange={onAdmissionChange}
                ProcessingStatusIcon={ProcessingStatusIcon}
                dedupMatches={dedupMatches}
                setDedupMatches={setDedupMatches}
                isMerging={isMerging}
                onMergeStart={() => setSelectedMatchId(String(dedupMatches[0]._id))}
                conflicts={conflicts}
                onConflictChoose={(key, value) => setConflictChoices((prev) => ({ ...prev, [key]: value }))}
              />
            </div>

            <MutuelleSection
              admission={admission}
              activeFields={activeFields}
              validationErrors={validationErrors}
              onAdmissionChange={onAdmissionChange}
              isMerging={isMerging}
              conflicts={conflicts}
              onConflictChoose={(key, value) => setConflictChoices((prev) => ({ ...prev, [key]: value }))}
              ProcessingStatusIcon={ProcessingStatusIcon}
            />

            <div inert={isMerging ? "" : undefined} className={`transition-opacity ${isMerging ? "opacity-40" : ""}`}>
              <SamplingSection admission={admission} activeFields={activeFields} onAdmissionChange={onAdmissionChange} />

              {admission?.prescriptions?.map((prescription, index) => (
                <div key={prescription._id} className="mb-3">
                  <PrescriptionCard
                    prescription={prescription}
                    index={index}
                    isSelected={selectedPrescriptions.has(prescription._id)}
                    activeFields={activeFields}
                    selectedCodes={selectedCodes}
                    setSelectedCodes={setSelectedCodes}
                    setContextMenu={setContextMenu}
                    renewalPopoverCode={renewalPopover?.prescriptionId === prescription._id ? renewalPopover.code : null}
                    onRenewalPopoverChange={(code) => setRenewalPopover(code ? { prescriptionId: prescription._id, code } : null)}
                    autocompleteSettings={autocompleteSettings}
                    loading={isPrescriptionLoading}
                    actionsApplied={actionsAppliedToPrescriptions.includes(prescription._id)}
                    incompatibleCodesRefused={incompatibleCodesRefused.filter((effect) => effect.prescriptionId === prescription._id)}
                    onToggleSelection={() => onTogglePrescriptionSelection(prescription._id)}
                    onAdmissionChange={onAdmissionChange}
                    onScrollToImage={onScrollToImage}
                    ProcessingStatusIcon={ProcessingStatusIcon}
                    admissionSelected={admission}
                    onAdmissionUpdated={onAdmissionUpdated}
                    getAdmissionDetails={onGetAdmissionDetails}
                    setLoading={setIsPrescriptionLoading}
                  />
                </div>
              ))}

              {!isLocked && (
                <button
                  type="button"
                  className={`mb-4 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    isPrescriptionMissing ? "border border-orange-300 bg-orange-50 text-orange-600 hover:bg-orange-100" : "text-gray-500 hover:bg-primary-50 hover:text-primary-600"
                  }`}
                  onClick={() => setShowAddPrescriptionModal(true)}
                  data-focus-field="prescriptions"
                >
                  <HiPlus className="h-3.5 w-3.5" />
                  Ajouter une ordonnance
                </button>
              )}

              <CommentsSection admissionSelected={admission} onAdmissionChange={onAdmissionChange} />

              <AdditionalDoctorsSection
                admission={admission}
                autocompleteSettings={autocompleteSettings}
                onAdmissionChange={onAdmissionChange}
                ProcessingStatusIcon={ProcessingStatusIcon}
              />

              <CorrespondantsSection
                admission={admission}
                autocompleteSettings={autocompleteSettings}
                onAdmissionChange={onAdmissionChange}
                ProcessingStatusIcon={ProcessingStatusIcon}
              />

              {admission?._id && (
                <div className="mb-4">
                  <QuestionsSection
                    selectedAdmissionId={admission?._id}
                    getAdmissionDetails={onGetAdmissionDetails}
                    setLoading={onSetBackgroundLoading}
                    showProductionRequired={showProductionRequired}
                    questionRecords={admission?.questionRecords}
                    loading={isBackgroundLoading}
                    isLocked={isLocked}
                    onQuestionRecordChange={(questionRecordIndex, value) => onAdmissionChange([{ field: `questionRecords.${admission.questionRecords[questionRecordIndex]._id}.answer`, value }])}
                    onScrollToImage={onScrollToImage}
                    ProcessingStatusIcon={ProcessingStatusIcon}
                  />
                </div>
              )}

              <RecommendationsSection guidelines={admission?.guidelines} onGuidelineValidatedChange={onGuidelineValidatedChange} />

              {validationErrors.length > 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-yellow-300 px-2 py-1 text-black">
                  <HiExclamationCircle className="h-4 w-4" />
                  Le dossier n&apos;a pas pu être enregistré, certains champs sont invalides
                </div>
              )}
            </div>
          </div>
        )}

        {isMerging && (
          <div className="animate-slide-up fixed bottom-6 left-1/2 z-40 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 transform">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_12px_32px_-12px_rgba(16,24,40,0.35)]">
              <label className="relative flex min-w-0 items-center gap-1 whitespace-nowrap border-b border-gray-100 py-1.5 pl-4 pr-2 text-xs text-gray-400">
                Fusion avec
                <select
                  value={selectedMatchId}
                  onChange={(event) => setSelectedMatchId(event.target.value)}
                  onKeyDown={(event) => event.key === "Escape" && setSelectedMatchId(null)}
                  disabled={dedupMatches.length === 1}
                  className="min-w-0 cursor-pointer appearance-none truncate rounded-md bg-transparent py-1 pl-1.5 pr-5 font-medium text-gray-800 outline-none hover:bg-gray-100 disabled:cursor-default disabled:pr-1.5 disabled:hover:bg-transparent"
                >
                  {dedupMatches.map((match, index) => {
                    const diffCount = conflictsWith(admission?.patient, match, activeFields).length;
                    const parts = [`${(match.lastName || "").toUpperCase()} ${match.firstName || ""}`.trim() || `Patient ${index + 1}`];
                    if (match.patientId || match.silPatientNumber) parts.push(`n° ${match.patientId || match.silPatientNumber}`);
                    if (match.INS?.isQualified === true) parts.push("INS qualifiée");
                    parts.push(diffCount === 0 ? "identique" : `${diffCount} diff.`);
                    return (
                      <option key={match._id || index} value={String(match._id)}>
                        {parts.join(" · ")}
                      </option>
                    );
                  })}
                </select>
                {dedupMatches.length > 1 && <LuChevronDown className="pointer-events-none -ml-5 h-3 w-3 shrink-0 text-gray-400" />}
              </label>
              {overriddenIdentityLabels.length > 0 && (
                <div className="flex items-center gap-1.5 border-b border-orange-100 bg-orange-50/70 px-4 py-1.5 text-xs text-orange-700">
                  <LuTriangleAlert className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    <span className="font-medium">Identité différente</span> sur {overriddenIdentityLabels.length} champ{overriddenIdentityLabels.length > 1 ? "s" : ""} (
                    {overriddenIdentityLabels.join(", ")}) : la fiche est conservée
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3 py-2 pl-4 pr-2">
                {choosableConflicts.length > 0 ? (
                  <>
                    <div className="flex items-center gap-1">
                      {choosableConflicts.map((conflict) => (
                        <span key={conflict.key} className={`h-1 w-5 rounded-full transition-colors ${conflict.choice ? "bg-gray-800" : "bg-orange-300"}`} />
                      ))}
                    </div>
                    <span className="whitespace-nowrap text-xs text-gray-500">
                      {choosableConflicts.length - pendingChoiceCount} / {choosableConflicts.length} choisis
                    </span>
                  </>
                ) : (
                  <span className="whitespace-nowrap text-xs text-gray-500">Aucune différence à trancher</span>
                )}
                <div className="ml-auto flex items-center gap-1.5 pl-6">
                  <kbd className="rounded border border-gray-200 bg-white px-1 font-sans text-[10px] text-gray-400">Échap</kbd>
                  <button
                    type="button"
                    onClick={() => setSelectedMatchId(null)}
                    className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={onLinkPatient}
                    disabled={isBackgroundLoading || pendingChoiceCount > 0}
                    title={pendingChoiceCount > 0 ? `${pendingChoiceCount} champ${pendingChoiceCount > 1 ? "s" : ""} à choisir avant de lier` : undefined}
                    className="flex shrink-0 items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <HiOutlineCheck className="h-3.5 w-3.5" />
                    Lier le patient
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AdmissionCodeContextMenu
          admission={admission}
          contextMenu={contextMenu}
          selectedCodes={selectedCodes}
          setSelectedCodes={setSelectedCodes}
          setContextMenu={setContextMenu}
          onAdmissionChange={onAdmissionChange}
          onRenewalMarked={(prescriptionId, code) => setRenewalPopover({ prescriptionId, code })}
        />

        {!isMerging && (
          <SelectedPrescriptionsBar
            selectedCount={selectedPrescriptions.size}
            loading={prescriptionBatchLoading}
            onDelete={() => setShowBatchDeletePrescriptionsModal(true)}
            onMerge={() => setShowPrescriptionMergeModal(true)}
            onClear={() => setSelectedPrescriptions(new Set())}
          />
        )}

        {showAddPrescriptionModal && (
          <AddPrescriptionModal
            loading={isBackgroundLoading}
            admissionSelectedId={admission?._id}
            setShowAddPrescriptionModal={setShowAddPrescriptionModal}
            getAdmissionDetails={onGetAdmissionDetails}
            setLoading={onSetBackgroundLoading}
          />
        )}

        {showBatchDeletePrescriptionsModal && (
          <BatchDeletePrescriptionsModal
            loading={prescriptionBatchLoading}
            setPrescriptionBatchLoading={setPrescriptionBatchLoading}
            selectedPrescriptions={selectedPrescriptions}
            setSelectedPrescriptions={setSelectedPrescriptions}
            setShowBatchDeletePrescriptionsModal={setShowBatchDeletePrescriptionsModal}
            admissionSelected={admission}
            getAdmissionDetails={onGetAdmissionDetails}
          />
        )}

        {showPrescriptionMergeModal && (
          <MergePrescriptionsModal
            selectedPrescriptions={selectedPrescriptions}
            admissionSelected={admission}
            setShowPrescriptionMergeModal={setShowPrescriptionMergeModal}
            setSelectedPrescriptions={setSelectedPrescriptions}
            getAdmissionDetails={onGetAdmissionDetails}
          />
        )}
      </>
    );
  },
);

AdmissionOverview.displayName = "AdmissionOverview";

export default AdmissionOverview;
